import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdDelete } from 'react-icons/md';
import { authenticatedFetch } from '../../../lib/api';
import { useToast } from '../../../components/shared/Toast';
import { Modal } from '../../../components/shared/Modal';
import { useForm } from 'react-hook-form';
import { erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { pmFieldClass, pmLabelClass, pmTextareaClass, pmDangerIconBtnClass, pmTableWrapClass } from '../port-management.ui';

const CHARGE_LABELS: Record<string, string> = {
    port_dues: 'Port Dues',
    wharfage: 'Wharfage / Dock',
    dry_dock: 'Dry Dock',
    parking: 'Parking',
    pilotage: 'Pilotage',
    towage: 'Towage',
    mooring: 'Mooring',
    utilities: 'Utilities',
    storage: 'Storage',
    other: 'Other',
};

export function RulesChargesUI({ section = 'rules' }: { section?: 'rules' | 'charges' }) {
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);
    const { success, error } = useToast();
    const queryClient = useQueryClient();

    // Fetch Rules
    const { data: rules = [] } = useQuery({
        queryKey: ['port-rules'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/rules');
            const data = await res.json();
            return data.data || [];
        },
    });

    // Fetch Charges
    const { data: charges = [] } = useQuery({
        queryKey: ['port-charges'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/charges');
            const data = await res.json();
            return data.data || [];
        },
    });

    // Delete Mutations
    const deleteRuleMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await authenticatedFetch(`/api/v1/rules/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete rule');
        },
        onSuccess: () => {
            success('Rule deleted');
            queryClient.invalidateQueries({ queryKey: ['port-rules'] });
        },
        onError: (err: any) => error(err.message)
    });

    const deleteChargeMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await authenticatedFetch(`/api/v1/charges/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete charge');
        },
        onSuccess: () => {
            success('Charge deleted');
            queryClient.invalidateQueries({ queryKey: ['port-charges'] });
        },
        onError: (err: any) => error(err.message)
    });

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-5 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <h3 className="text-base font-semibold text-[hsl(var(--foreground))]">
                        {section === 'rules' ? 'Port rules' : 'Tariffs & charges'}
                    </h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {section === 'rules'
                            ? 'Compliance and operating constraints'
                            : 'Port charges and fee schedules'}
                    </p>
                </div>
                {section === 'rules' ? (
                    <button type="button" onClick={() => setIsRuleModalOpen(true)} className={erpPageBtnPrimaryClass}>
                        <MdAdd className="h-5 w-5" />
                        Add rule
                    </button>
                ) : (
                    <button type="button" onClick={() => setIsChargeModalOpen(true)} className={erpPageBtnPrimaryClass}>
                        <MdAdd className="h-5 w-5" />
                        Add charge
                    </button>
                )}
            </div>

            <div className={pmTableWrapClass}>
            {section === 'rules' ? (
                <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-3">
                    {rules.map((rule: any) => (
                        <div key={rule._id} className="flex min-h-[168px] flex-col rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#0088cc]">{rule.ruleType}</p>
                                    <h4 className="mt-1 truncate text-sm font-semibold text-[hsl(var(--foreground))]">{rule.portId?.name || '—'}</h4>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (window.confirm('Delete this rule?')) deleteRuleMutation.mutate(rule._id);
                                    }}
                                    className={pmDangerIconBtnClass}
                                    aria-label="Delete rule"
                                >
                                    <MdDelete className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="mt-2 line-clamp-3 flex-1 text-sm text-[hsl(var(--muted-foreground))]">{rule.description}</p>
                            <div className="mt-3 flex min-h-[28px] items-center justify-between gap-2 border-t border-[hsl(var(--border))] pt-3">
                                {rule.value ? (
                                    <span className="truncate rounded-md bg-[hsl(var(--muted))] px-2 py-1 text-xs font-medium">{rule.value}</span>
                                ) : <span />}
                                {rule.isMandatory && (
                                    <span className="shrink-0 rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
                                        Mandatory
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                    {rules.length === 0 && (
                        <div className="col-span-full rounded-lg border border-dashed border-[hsl(var(--border))] py-16 text-center text-sm text-[hsl(var(--muted-foreground))]">
                            No rules yet
                        </div>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-3">
                    {charges.map((charge: any) => (
                        <div key={charge._id} className="flex min-h-[168px] flex-col rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#0088cc]">
                                        {CHARGE_LABELS[charge.chargeCategory] || charge.chargeCategory || 'Other'}
                                    </p>
                                    <h4 className="mt-1 truncate text-sm font-semibold text-[hsl(var(--foreground))]">{charge.chargeName}</h4>
                                    <p className="mt-0.5 truncate text-xs text-[hsl(var(--muted-foreground))]">{charge.portId?.name || '—'}</p>
                                </div>
                                <div className="shrink-0 text-right">
                                    <p className="text-base font-semibold text-[#0088cc]">
                                        {charge.amount} <span className="text-xs font-medium text-[hsl(var(--muted-foreground))]">{charge.currency}</span>
                                    </p>
                                    {charge.unit && <p className="text-xs text-[hsl(var(--muted-foreground))]">{charge.unit}</p>}
                                </div>
                            </div>
                            <p className="mt-2 line-clamp-2 flex-1 text-sm text-[hsl(var(--muted-foreground))]">{charge.description || ' '}</p>
                            <button
                                type="button"
                                onClick={() => {
                                    if (window.confirm('Delete this charge?')) deleteChargeMutation.mutate(charge._id);
                                }}
                                className="mt-3 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--muted-foreground))] hover:border-red-200 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
                            >
                                <MdDelete className="h-4 w-4" />
                                Delete
                            </button>
                        </div>
                    ))}
                    {charges.length === 0 && (
                        <div className="col-span-full rounded-lg border border-dashed border-[hsl(var(--border))] py-16 text-center text-sm text-[hsl(var(--muted-foreground))]">
                            No charges yet
                        </div>
                    )}
                </div>
            )}
            </div>

            <Modal isOpen={isRuleModalOpen} onClose={() => setIsRuleModalOpen(false)} title="Add port rule">
                <RuleForm onSuccess={() => { setIsRuleModalOpen(false); queryClient.invalidateQueries({ queryKey: ['port-rules'] }); }} onCancel={() => setIsRuleModalOpen(false)} />
            </Modal>

            <Modal isOpen={isChargeModalOpen} onClose={() => setIsChargeModalOpen(false)} title="Add port charge">
                <ChargeForm onSuccess={() => { setIsChargeModalOpen(false); queryClient.invalidateQueries({ queryKey: ['port-charges'] }); }} onCancel={() => setIsChargeModalOpen(false)} />
            </Modal>
        </div>
    );
}

function RuleForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
    const { register, handleSubmit, formState: { isSubmitting } } = useForm();
    const { success, error } = useToast();

    const { data: ports = [] } = useQuery({
        queryKey: ['ports-dropdown'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports?status=Active&limit=1000');
            const data = await res.json();
            return data.data || [];
        },
    });

    const onSubmit = async (data: any) => {
        try {
            const res = await authenticatedFetch('/api/v1/rules', {
                method: 'POST',
                body: JSON.stringify(data),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed to create rule');
            success('Rule created');
            onSuccess();
        } catch (err: any) {
            error(err.message);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
                <label className={pmLabelClass}>Port *</label>
                <select {...register('portId', { required: true })} className={pmFieldClass}>
                    <option value="">Select port</option>
                    {ports.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
            </div>
            <div>
                <label className={pmLabelClass}>Rule type *</label>
                <input {...register('ruleType', { required: true })} placeholder="e.g. Delivery, Security" className={pmFieldClass} />
            </div>
            <div>
                <label className={pmLabelClass}>Description *</label>
                <textarea {...register('description', { required: true })} className={pmTextareaClass} rows={3} />
            </div>
            <div>
                <label className={pmLabelClass}>Value (optional)</label>
                <input {...register('value')} placeholder="e.g. 24/7, Max 10 tons" className={pmFieldClass} />
            </div>
            <div className="flex items-center gap-2">
                <input type="checkbox" {...register('isMandatory')} id="isMandatory" className="h-4 w-4 rounded border-[hsl(var(--border))] text-[#0088cc] focus:ring-[#0088cc]" />
                <label htmlFor="isMandatory" className="text-sm text-[hsl(var(--foreground))]">Mandatory</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className={erpPageBtnSecondaryClass}>Cancel</button>
                <button type="submit" disabled={isSubmitting} className={erpPageBtnPrimaryClass}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                </button>
            </div>
        </form>
    );
}

function ChargeForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
    const { register, handleSubmit, formState: { isSubmitting } } = useForm({
        defaultValues: { currency: 'USD', chargeCategory: 'other', amount: 0 },
    });
    const { success, error } = useToast();

    const { data: ports = [] } = useQuery({
        queryKey: ['ports-dropdown'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports?status=Active&limit=1000');
            const data = await res.json();
            return data.data || [];
        },
    });

    const onSubmit = async (data: any) => {
        try {
            const res = await authenticatedFetch('/api/v1/charges', {
                method: 'POST',
                body: JSON.stringify({ ...data, amount: Number(data.amount) }),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed to create charge');
            success('Charge created');
            onSuccess();
        } catch (err: any) {
            error(err.message);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
                <label className={pmLabelClass}>Port *</label>
                <select {...register('portId', { required: true })} className={pmFieldClass}>
                    <option value="">Select port</option>
                    {ports.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
            </div>
            <div>
                <label className={pmLabelClass}>Charge category *</label>
                <select {...register('chargeCategory', { required: true })} className={pmFieldClass}>
                    {Object.entries(CHARGE_LABELS).map(([id, label]) => (
                        <option key={id} value={id}>{label}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className={pmLabelClass}>Charge name *</label>
                <input {...register('chargeName', { required: true })} className={pmFieldClass} />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Amount *</label>
                    <input type="number" step="any" {...register('amount', { required: true })} className={pmFieldClass} />
                </div>
                <div>
                    <label className={pmLabelClass}>Currency *</label>
                    <input {...register('currency', { required: true })} defaultValue="USD" className={pmFieldClass} />
                </div>
            </div>
            <div>
                <label className={pmLabelClass}>Unit</label>
                <input {...register('unit')} placeholder="e.g. per ton, per day" className={pmFieldClass} />
            </div>
            <div>
                <label className={pmLabelClass}>Description</label>
                <textarea {...register('description')} className={pmTextareaClass} rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className={erpPageBtnSecondaryClass}>Cancel</button>
                <button type="submit" disabled={isSubmitting} className={erpPageBtnPrimaryClass}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                </button>
            </div>
        </form>
    );
}
